import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Bell, X, Calendar as CalendarIcon, Clock, Check, MailQuestion, UserPlus,
    UserCheck, Info, Wallet, KanbanSquare, FolderOpen, StickyNote, Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { calendarService } from '../services/calendarService';
import contactService from '../services/contactService';
import { useLanguage } from '../i18n';
import ReminderAttachmentList from './ReminderAttachmentList';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useNotificationFeed } from '../hooks/useNotificationFeed';

// ── Native per-feature Bell treatments (#381 §2) ────────────────────────────
// A DISPATCHED notification's row now carries its FEATURE as `kind` — see
// `notification_prefs.bell_kind`. That change exists to keep the row out of the
// reminder-push claim (it used to be minted as `kind: 'reminder'`, so the cron
// re-pushed it hours-of-work later wearing a "⏰ Reminder:" template), and it
// hands the Bell something to style on: each feature reads as itself instead of
// borrowing the Reminder clock or falling through to the generic "System
// notification" badge.
//
// An unlisted kind still falls through to that generic treatment, so a feature
// added server-side is never blank — it just isn't branded until it's listed.
export const FEATURE_KINDS = {
    finance: { icon: Wallet, i18n: ['navBadgeFinance', 'Finance'] },
    boards: { icon: KanbanSquare, i18n: ['navBadgeBoards', 'Boards'] },
    vault: { icon: FolderOpen, i18n: ['navBadgeVault', 'File Manager'] },
    notes: { icon: StickyNote, i18n: ['navBadgeNotes', 'Notes'] },
    network: { icon: Users, i18n: ['navBadgeNetwork', 'Network'] },
    calendar: { icon: CalendarIcon, i18n: ['navBadgeCalendar', 'Calendar'] },
};

const pad = (n) => String(n).padStart(2, '0');

// Plain helper (not a component) — `t` is threaded in as a parameter so it can
// still be called outside of React render (the hook itself only works inside
// a component/custom hook).
export const formatRemindAt = (iso, t) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = date - now;
    const diffMin = Math.round(diffMs / 60000);

    const absMin = Math.abs(diffMin);
    const hours = date.getHours();
    const mins = date.getMinutes();
    const timeStr = `${pad(hours)}:${pad(mins)}`;

    if (diffMin <= -60 * 24) {
        return `${date.toLocaleDateString()} ${timeStr}`;
    }
    if (diffMin < 0) {
        if (absMin < 60) return t('navMinAgo', '{n} min ago', { n: absMin });
        return t('navHourAgo', '{n}h ago', { n: Math.round(absMin / 60) });
    }
    if (diffMin < 1) return t('navNow', 'Now');
    if (diffMin < 60) return t('navInMin', 'In {n} min', { n: diffMin });

    const sameDay =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

    if (sameDay) return t('navTodayAt', 'Today {time}', { time: timeStr });

    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow =
        date.getFullYear() === tomorrow.getFullYear() &&
        date.getMonth() === tomorrow.getMonth() &&
        date.getDate() === tomorrow.getDate();

    if (isTomorrow) return t('navTomorrowAt', 'Tomorrow {time}', { time: timeStr });

    return `${date.toLocaleDateString()} ${timeStr}`;
};

// Per-row component so kind='event_invitation' rows can carry local
// Confirm/Decline busy state without lifting it into the parent.
//
// `variant` (#437): 'dropdown' is the original compact tray row (still used by the
// day-channel focus view's bell); 'box' is the Notifications column of the
// Reminders & Notifications panel — the SAME classification, testids and actions,
// drawn as a reminder-card-sized box with an always-visible X on every row.
export const ReminderRow = ({ item, onBodyClick, onClear, onAfterRespond, onOpenAttachment, variant = 'dropdown' }) => {
    const isBox = variant === 'box';
    const { t } = useLanguage();
    const [busy, setBusy] = useState(false);
    const isCalendar = item.type === 'calendar_event';
    const isInvitation = item.kind === 'event_invitation';
    // #536 (PO 2026-11-02): ONE numbered invitation to more than 5 events —
    // the backend marks its Bell link with `invite_package=<n>`. Its buttons
    // answer ALL of them; the hint says how to answer only some, in text.
    const isPackageInvitation = isInvitation && /[?&]invite_package=\d+/.test(item.link || '');
    // Agenda TO Contacts (Phase A): an inbound contact handshake — Accept/Decline
    // inline, mirroring the calendar-invitation row. `contact_request_accepted` is
    // the passive feedback row shown to the requester once the other side accepts.
    const isContactRequest = item.kind === 'contact_request';
    const isContactAccepted = item.kind === 'contact_request_accepted';
    const isFeedback = item.kind === 'event_response_confirmed' || item.kind === 'event_response_declined';
    // Notification Bug (#346): ANY kind other than the genuine, user-requested
    // 'reminder' — and not one of the already-distinct interactive kinds above
    // — is an automated SYSTEM notice (background-delete completion, a 80%
    // quota warning, an OCR-clarification ask, "set the time" for an untimed
    // event, a low-energy alert, …). It must never render with the Reminder
    // icon/label. Checking "not one of the known kinds" rather than a single
    // hardcoded `kind === 'system'` means a NEW system kind (any future
    // feature) is covered automatically, with no FE change required.
    const isSystem = !isCalendar && !isInvitation && !isContactRequest
        && !isContactAccepted && !isFeedback
        && item.kind && item.kind !== 'reminder';
    // #381 §2 — a feature-kind row gets that feature's own treatment. Still a
    // system notice (never the Reminder look), just a branded one.
    const feature = isSystem ? FEATURE_KINDS[item.kind] : undefined;

    const respond = async (decision, e) => {
        e?.stopPropagation();
        if (!item.invitation_id || busy) return;
        setBusy(true);
        try {
            await calendarService.respondToInvitation(item.invitation_id, decision);
            onAfterRespond?.();
        } catch (_) {
            // re-enable so the user can retry
        } finally {
            setBusy(false);
        }
    };

    // Accept / Decline an inbound contact request. Mirrors `respond` above:
    // busy-guard, on success let the parent refetch (the BE clears this bell row)
    // and broadcast so the Agenda panel + any contact listeners refresh.
    const respondContact = async (decision, e) => {
        e?.stopPropagation();
        if (!item.contact_request_id || busy) return;
        setBusy(true);
        try {
            await contactService.respondRequest(item.contact_request_id, decision);
            window.dispatchEvent(new CustomEvent('omnis:contact-changed'));
            window.dispatchEvent(new CustomEvent('omnis:contact-request-resolved'));
            onAfterRespond?.();
        } catch (_) {
            // re-enable so the user can retry
        } finally {
            setBusy(false);
        }
    };

    let accentBg = 'hover:bg-white/[0.07]';
    let accentText = 'text-pai-blue';
    let icon = <Clock size={16} />;
    if (isCalendar) {
        accentBg = 'bg-[rgba(245,165,36,0.06)]';
        accentText = 'text-pai-amber';
        icon = <CalendarIcon size={16} />;
    } else if (isInvitation) {
        accentBg = 'bg-[rgba(74,144,240,0.07)]';
        accentText = 'text-pai-blue';
        icon = <MailQuestion size={16} />;
    } else if (isContactRequest) {
        accentBg = 'bg-[rgba(74,144,240,0.07)]';
        accentText = 'text-pai-blue';
        icon = <UserPlus size={16} />;
    } else if (isContactAccepted) {
        accentBg = 'bg-emerald-50/50 dark:bg-emerald-900/15';
        accentText = 'text-emerald-600 dark:text-emerald-300';
        icon = <UserCheck size={16} />;
    } else if (isFeedback) {
        accentBg = item.kind === 'event_response_confirmed'
            ? 'bg-emerald-50/50 dark:bg-emerald-900/15'
            : 'bg-rose-50/50 dark:bg-rose-900/15';
        accentText = item.kind === 'event_response_confirmed'
            ? 'text-emerald-600 dark:text-emerald-300'
            : 'text-rose-600 dark:text-rose-300';
        icon = item.kind === 'event_response_confirmed' ? <Check size={16} /> : <X size={16} />;
    } else if (isSystem) {
        // A distinct, neutral treatment — deliberately NOT the reminder Clock —
        // so an automated notice is never mistaken for something the user
        // asked to be reminded about.
        accentBg = 'bg-white/[0.03]';
        accentText = 'text-pai-meta';
        const FeatureIcon = feature?.icon;
        icon = FeatureIcon ? <FeatureIcon size={16} /> : <Info size={16} />;
    }

    return (
        <div
            onClick={() => onBodyClick(item)}
            data-testid={isContactRequest ? 'bell-contact-request' : (isContactAccepted ? 'bell-contact-request-accepted' : (isSystem ? 'bell-system-notification' : undefined))}
            data-variant={variant}
            className={isBox
                // The Reminders card's box: same radius, padding, type scale and
                // hairline border (#437 "visually identical in size and style").
                ? `group rounded-row border border-white/[0.09] px-[18px] py-4 flex items-start gap-3 transition-colors duration-150 hover:border-white/[0.16] ${
                    item.link ? 'cursor-pointer' : ''
                } ${accentBg === 'hover:bg-white/[0.07]' ? 'bg-white/[0.025] hover:bg-white/[0.06]' : accentBg}`
                : `group px-4 py-3 border-b border-pai-hairline last:border-0 flex items-start gap-3 ${
                    item.link ? 'cursor-pointer' : ''
                } ${accentBg}`}
        >
            <div className={`mt-0.5 ${accentText}`}>{icon}</div>
            <div className="flex-1 min-w-0" data-testid="bell-item" data-bell-title={item.title} data-bell-kind={item.kind || 'reminder'}>
                <div className={isBox
                    ? 'text-[15px] font-semibold leading-[1.35] text-white break-words'
                    : 'text-sm font-medium text-white break-words'}
                >
                    {item.title}
                </div>
                <div className={isBox
                    ? 'text-[12.5px] text-white/[0.42] mt-1.5 flex flex-wrap items-center gap-2'
                    : 'text-xs text-pai-meta mt-0.5 flex items-center gap-2'}
                >
                    <span>{formatRemindAt(item.remind_at, t)}</span>
                    {isCalendar && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 font-semibold">{t('navBadgeCalendar', 'Calendar')}</span>
                    )}
                    {isCalendar && item.location && <span className="truncate">· {item.location}</span>}
                    {isInvitation && (
                        <span className="text-[10px] uppercase tracking-wide text-pai-blue font-semibold">{t('navBadgeInvitation', 'Invitation')}</span>
                    )}
                    {isContactRequest && (
                        <span className="text-[10px] uppercase tracking-wide text-pai-blue font-semibold">{t('navBadgeContactRequest', 'Contact request')}</span>
                    )}
                    {isSystem && (
                        <span data-testid="bell-system-badge" className="text-[10px] uppercase tracking-wide text-pai-faint font-semibold">
                            {feature ? t(feature.i18n[0], feature.i18n[1]) : t('navBadgeSystem', 'System notification')}
                        </span>
                    )}
                </div>
                {/* Attached documents (#386) — a fired reminder shows the files
                    the user attached so they have the context; clicking one opens
                    it in the preview modal (the list stops propagation, so it
                    never also follows the row's link). */}
                {!isCalendar && (item.kind || 'reminder') === 'reminder' && item.attachments?.length > 0 && (
                    <ReminderAttachmentList
                        className="mt-2"
                        attachments={item.attachments}
                        onOpen={onOpenAttachment}
                    />
                )}
                {isInvitation && (
                    <div className="mt-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={(e) => respond('confirmed', e)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 rounded"
                        >
                            <Check size={11} />
                            {isPackageInvitation ? t('invitePkgAcceptAll', 'Accept all') : t('commonConfirm', 'Confirm')}
                        </button>
                        <button
                            onClick={(e) => respond('declined', e)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] text-rose-700 dark:text-rose-300 bg-white/[0.03] hover:bg-rose-50 dark:hover:bg-rose-900/30 border border-rose-300 dark:border-rose-700 disabled:opacity-50 rounded"
                        >
                            <X size={11} />
                            {isPackageInvitation ? t('invitePkgDeclineAll', 'Decline all') : t('pendingDeclineBtn', 'Decline')}
                        </button>
                    </div>
                )}
                {isPackageInvitation && (
                    <p data-testid="bell-invitation-package-hint" className="mt-1.5 text-[11px] text-pai-meta break-words">
                        {t('invitePkgHint')}
                    </p>
                )}
                {isContactRequest && (
                    <div className="mt-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                            data-testid="bell-contact-accept"
                            onClick={(e) => respondContact('accept', e)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 rounded"
                        >
                            <Check size={11} />
                            {t('pendingAcceptBtn', 'Accept')}
                        </button>
                        <button
                            data-testid="bell-contact-decline"
                            onClick={(e) => respondContact('decline', e)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] text-rose-700 dark:text-rose-300 bg-white/[0.03] hover:bg-rose-50 dark:hover:bg-rose-900/30 border border-rose-300 dark:border-rose-700 disabled:opacity-50 rounded"
                        >
                            <X size={11} />
                            {t('pendingDeclineBtn', 'Decline')}
                        </button>
                    </div>
                )}
            </div>
            {/* The tray hides the X until hover and never offers it on an
                actionable invitation; the box shows a clear X on EVERY row
                (#437) — clearing an invitation's row only removes the
                notice, the invitation itself stays answerable from the
                Calendar's Event Invitations list and the Contacts hub. */}
            {(isBox || (!isInvitation && !isContactRequest)) && (
                <button
                    onClick={(e) => onClear(item, e)}
                    data-testid="bell-dismiss"
                    aria-label={t('navDismissNotification', 'Dismiss notification')}
                    className={isBox
                        ? 'pai-icon-btn w-[30px] h-[30px] flex-shrink-0 -mr-1 -mt-1 text-pai-meta hover:text-white'
                        : 'opacity-0 group-hover:opacity-100 pai-touch-reveal transition-opacity p-1 text-pai-faint hover:text-white '}
                    title={t('navDismissNotification', 'Dismiss notification')}
                >
                    <X size={isBox ? 16 : 14} />
                </button>
            )}
        </div>
    );
};

const RemindersDropdown = () => {
    const { t } = useLanguage();
    // The feed itself (fetch / poll / live refetch / dismissal) lives in
    // useNotificationFeed since #437, shared with the Notifications column.
    const { items, loading, error, refresh: fetchItems, clear } = useNotificationFeed();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    // The chat pane clips to its rounded glass corners (`overflow-hidden`), so an
    // absolutely-positioned dropdown inside the header is sliced away. Portal it
    // to <body> and anchor it to the bell — the same fix #249 applied to the
    // ledger Options menu.
    const bellRef = useRef(null);
    const menuRef = useRef(null);
    const [pos, setPos] = useState(null);
    // The attachment being previewed (#386). Rendered at the component root, NOT
    // inside the portaled tray: the tray closes on any outside mousedown, and the
    // preview must outlive it.
    const [preview, setPreview] = useState(null);
    const navigate = useNavigate();
    // Escape closes the preview only — never a panel PanelShell would also
    // close on the same key (the #272 lesson).
    useEffect(() => {
        if (!preview) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); setPreview(null); }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [preview]);

    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            // The menu is PORTALED, so it is not inside wrapperRef — without this
            // second check every click on a notification would close the tray
            // before its own handler ran.
            const inTrigger = wrapperRef.current?.contains(e.target);
            const inMenu = menuRef.current?.contains(e.target);
            if (!inTrigger && !inMenu) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        if (next) fetchItems();
    };

    const handleClear = (item, e) => {
        e.stopPropagation();
        clear(item);
    };

    const handleBodyClick = (item) => {
        // Deep link: clicking the notification body jumps to the linked
        // route (e.g. the Focus Overlay for a freshly logged Unassigned
        // transaction). Falls back to no-op when no link is attached.
        if (!item.link) return;
        setOpen(false);
        navigate(item.link);
    };

    useEffect(() => {
        if (!open) { setPos(null); return; }
        const place = () => {
            const r = bellRef.current?.getBoundingClientRect();
            if (!r) return;
            setPos({ top: Math.round(r.bottom + 8), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open]);

    const count = items.length;

    return (
        <div ref={wrapperRef} className="relative">
            <button
                ref={bellRef}
                data-testid="bell-button"
                onClick={handleToggle}
                className="relative p-2 text-pai-meta hover:text-white hover:bg-white/[0.07] rounded-lg transition-colors"
                title={t('remindersColumnTitle', 'Reminders')}
            >
                <Bell size={20} />
                {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-rose-500 rounded-full">
                        {count > 99 ? '99+' : count}
                    </span>
                )}
            </button>

            {open && pos && createPortal(
                <div
                    ref={menuRef}
                    data-testid="bell-dropdown"
                    className="fixed w-80 max-h-[420px] overflow-hidden bg-pai-modal border border-pai rounded-ctl shadow-modal-sm z-[70] flex flex-col"
                    style={{ top: pos.top, right: pos.right }}
                >
                    <div className="px-4 py-3 border-b border-pai-subtle flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-white">{t('remindersColumnTitle', 'Reminders')}</h3>
                        <span className="text-xs text-pai-meta">
                            {t('navActiveCount', '{n} active', { n: count })}
                        </span>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {error && (
                            <div className="px-4 py-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20">
                                {error}
                            </div>
                        )}
                        {loading && items.length === 0 && (
                            <div className="px-4 py-6 text-center text-sm text-pai-meta">
                                {t('navLoadingEllipsis', 'Loading…')}
                            </div>
                        )}
                        {!loading && items.length === 0 && !error && (
                            <div className="px-4 py-8 text-center">
                                <Bell size={24} className="mx-auto mb-2 text-white/25" />
                                <p className="text-sm text-pai-meta">
                                    {t('navNoReminders', 'No reminders')}
                                </p>
                                <p className="text-xs text-pai-faint mt-1">
                                    {t('navReminderExample', 'Try "remind me to call Charlie at 3pm"')}
                                </p>
                            </div>
                        )}

                        {items.map((item) => (
                            <ReminderRow
                                key={`${item.type}-${item.id}`}
                                item={item}
                                onBodyClick={handleBodyClick}
                                onClear={handleClear}
                                onOpenAttachment={(att) => {
                                    setOpen(false);
                                    setPreview(att);
                                }}
                                onAfterRespond={() => {
                                    fetchItems();
                                    // Responding flips the event status and (on
                                    // accept) dual-writes the event onto this
                                    // user's own calendar — refresh any open
                                    // calendar view immediately (Calendar
                                    // Confirmation card — Instant Updates).
                                    window.dispatchEvent(new CustomEvent('omnis:calendar-event-updated'));
                                }}
                            />
                        ))}
                    </div>
                </div>,
                document.body,
            )}
            {preview && (
                <DocumentPreviewModal
                    documentId={preview.document_id}
                    documentName={preview.filename}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>
    );
};

export default RemindersDropdown;
